import { SB } from "@play-co/replicant";
import { createError } from "../errorUtils.mjs";
import { createAsyncGettersForAnalytics } from "./asyncGetters.mjs";
import { stateSchema, actions, extendOnWebhookHandler } from "./configureTelegramAdEntryReply.mjs";
import { telegramStateSchema, telegramActions } from "./configureTelegramPlayerPhoto.mjs";
import { webPushActions } from "./configureWebPushSubscriptionHook.mjs";
import { getTemporalUserProperties } from "./getTemporalUserProperties.mjs";
import { storageAdapterState, storageAdapterActions } from "./storageAdapter.mjs";
function extendReplicantConfigForAnalytics(config, opts) {
  var _a, _b;
  const { getPlatformStorage } = opts || {};
  const extraState = {};
  const extraActions = { ...webPushActions };
  if (getPlatformStorage) {
    try {
      const defaultState = config.stateSchema.getDefault();
      const platformStorage = getPlatformStorage(defaultState);
      const platformStorageIsValid = platformStorage === void 0 || typeof platformStorage === "object";
      if (!platformStorageIsValid) {
        throw Error(`invalid value (${platformStorage})`);
      }
    } catch (error) {
      throw Error(
        `Cannot locate platform storage in Replicant state schema: ${error.message}. Check the getPlatformStorage option.`
      );
    }
  } else {
    Object.assign(extraState, storageAdapterState);
    Object.assign(extraActions, storageAdapterActions);
  }
  Object.assign(
    extraState,
    telegramStateSchema.getFullSchema(),
    stateSchema.getFullSchema()
  );
  Object.assign(
    extraActions,
    telegramActions,
    actions
  );
  const fullStateSchema = config.stateSchema.getFullSchema();
  const collidingStateKeys = Object.keys(extraState).filter(
    (key) => fullStateSchema[key]
  );
  if (collidingStateKeys.length > 0) {
    throw Error(
      // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
      `Cannot extend Replicant state schema with ${collidingStateKeys}: keys already exists in local configuration. Try renaming the state properties.`
    );
  }
  const stateSchema$1 = SB.object({ ...fullStateSchema, ...extraState });
  const extraAsyncGetters = createAsyncGettersForAnalytics({
    computedProperties: config.computedProperties || {},
    getPlatformStorage
  });
  const collidingAsyncGetterKeys = Object.keys(extraAsyncGetters).filter(
    (key) => {
      var _a2;
      return (_a2 = config.asyncGetters) == null ? void 0 : _a2[key];
    }
  );
  if (collidingAsyncGetterKeys.length > 0) {
    throw Error(
      // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
      `Cannot extend Replicant async getters configuration with ${collidingAsyncGetterKeys}: keys already exists in local configuration. Try renaming the async getters.`
    );
  }
  const collidingActionKeys = Object.keys(extraActions).filter(
    (key) => {
      var _a2;
      return (_a2 = config.actions) == null ? void 0 : _a2[key];
    }
  );
  if (collidingActionKeys.length > 0) {
    throw Error(
      // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
      `Cannot extend Replicant actions configuration with ${collidingActionKeys}: keys already exists in local configuration. Try renaming the actions.`
    );
  }
  if (config.getReceiverUserProperties) {
    throw Error(
      `Cannot extend Replicant configuration with getReceiverUserProperties: key already exists in local configuration.`
    );
  }
  let amplitudeTimeZone = opts == null ? void 0 : opts.amplitudeTimeZone;
  try {
    if (process.env.AMPLITUDE_TIME_ZONE) {
      amplitudeTimeZone = process.env.AMPLITUDE_TIME_ZONE;
    }
  } catch {
  }
  if (!amplitudeTimeZone) {
    throw createError(
      "INVALID_PARAM",
      "amplitudeTimeZone is undefined: define the value either in extendReplicantConfigForAnalytics parameters or with the AMPLITUDE_TIME_ZONE environment variable"
    );
  }
  let chatbotConfig = config.chatbot;
  if (opts == null ? void 0 : opts.makeTelegramAdReplyMessage) {
    const onWebhook = ((_a = config.chatbot) == null ? void 0 : _a.events.onWebhook) ?? (() => void 0);
    const extendedOnWebhook = extendOnWebhookHandler(
      onWebhook,
      opts.makeTelegramAdReplyMessage
    );
    chatbotConfig = {
      ...config.chatbot,
      events: {
        ...(_b = config.chatbot) == null ? void 0 : _b.events,
        onWebhook: extendedOnWebhook
      }
    };
  }
  return {
    ...config,
    stateSchema: stateSchema$1,
    actions: {
      ...config.actions,
      ...extraActions
    },
    asyncGetters: {
      ...config.asyncGetters,
      ...extraAsyncGetters
    },
    chatbot: chatbotConfig,
    getReceiverUserProperties: (receiverState, api) => getTemporalUserProperties(
      getPlatformStorage ? getPlatformStorage(receiverState) : receiverState.gcinstant.platformStorage,
      api.date.now(),
      amplitudeTimeZone
    ) || {},
    sendActionAnalyticsWithClient: true
  };
}
export {
  extendReplicantConfigForAnalytics
};
